import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Cake,
  CalendarHeart,
  GraduationCap,
  Home,
  Music,
  PlayCircle,
  Search,
  Sparkles,
} from "lucide-react";
import { getTemplates } from "../api/templates";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";
import { TemplateAutoVideo, TemplateVideoModal } from "../components/TemplateVideoPreview";

const EVENT_CATEGORIES = [
  {
    id: "wedding",
    title: "Wedding",
    description: "Multi-day storytelling for grand celebrations, ceremonies, and family moments.",
    accent: "from-[#6e1f43] via-[#b43775] to-[#f295b9]",
    icon: CalendarHeart,
  },
  {
    id: "birthday",
    title: "Birthday",
    description: "Playful or elegant invitation films for kids, milestones, and intimate parties.",
    accent: "from-[#17426e] via-[#2c77ba] to-[#7bc7ff]",
    icon: Cake,
  },
  {
    id: "baby-shower",
    title: "Baby Shower",
    description: "Soft, heartfelt designs with room for family details, blessings, and photo moments.",
    accent: "from-[#835b6c] via-[#c98aa6] to-[#f8cad9]",
    icon: Sparkles,
  },
  {
    id: "graduation",
    title: "Graduation",
    description: "Proud, modern invite films that spotlight achievements, dates, and celebration details.",
    accent: "from-[#294233] via-[#3f7c5a] to-[#9dd2b1]",
    icon: GraduationCap,
  },
  {
    id: "housewarming",
    title: "Housewarming",
    description: "Warm, welcoming invites for home blessings, griha pravesh, and family gatherings.",
    accent: "from-[#7a4a24] via-[#c68342] to-[#f0c28a]",
    icon: Home,
  },
  {
    id: "cultural-programs",
    title: "Cultural Programs",
    description: "Vibrant invitation films for festivals, dance shows, music nights, and community events.",
    accent: "from-[#5a2378] via-[#b544a2] to-[#f6a84f]",
    icon: Music,
  },
  {
    id: "engagement",
    title: "Engagement",
    description: "Elegant announcement films for ring ceremonies, family gatherings, and couple stories.",
    accent: "from-[#6d3b52] via-[#b56886] to-[#f3b8c8]",
    icon: CalendarHeart,
  },
  {
    id: "corporate",
    title: "Corporate",
    description: "Polished event pages and invitation videos for launches, dinners, and milestone events.",
    accent: "from-[#272f45] via-[#44537c] to-[#95a7d6]",
    icon: Briefcase,
  },
];

const PLATFORM_HIGHLIGHTS = [
  {
    value: "Video-first",
    label: "Preview motion and pacing before choosing a template.",
  },
  {
    value: "Guest-aware",
    label: "Personalize wedding chapters, private media, and group access later.",
  },
  {
    value: "Polotno-powered",
    label: "Customize approved templates without losing animation control.",
  },
];

const FEATURED_ROWS = [
  {
    id: "wedding",
    title: "Trending Wedding Invitation Films",
    blurb: "Rich, cinematic invitation styles for multi-function celebrations and luxury storytelling.",
  },
  {
    id: "birthday",
    title: "Popular Birthday Video Invitations",
    blurb: "Lively and memorable templates that feel personal before guests even RSVP.",
  },
  {
    id: "modern",
    title: "Modern Motion Designs",
    blurb: "Clean layouts, polished transitions, and versatile invitation films for any celebration.",
  },
  {
    id: "cultural-programs",
    title: "Cultural Program Highlights",
    blurb: "Colorful video invitations for performances, festivals, school events, and community celebrations.",
  },
];

function normalizeEventType(value) {
  return `${value || ""}`.toLowerCase().replace(/_/g, "-");
}

function getTemplateSearchText(template) {
  return [
    template?.name,
    template?.event_type,
    template?.category,
    template?.description,
    ...(Array.isArray(template?.style_tags) ? template.style_tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function doesTemplateMatchCategory(template, categoryId) {
  return normalizeEventType(template?.event_type || template?.category) === categoryId;
}

function getCategoryTemplateCount(templates, categoryId) {
  return templates.filter((template) => doesTemplateMatchCategory(template, categoryId)).length;
}

function pickTemplatesForRow(templates, row) {
  if (!templates.length) {
    return [];
  }

  if (row.id === "modern") {
    const styleMatches = templates.filter((template) =>
      (template.style_tags || []).some((tag) =>
        ["modern", "clean", "minimal"].includes(`${tag || ""}`.toLowerCase())
      )
    );

    return (styleMatches.length ? styleMatches : templates).slice(0, 8);
  }

  const eventMatches = templates.filter(
    (template) => normalizeEventType(template.event_type) === row.id
  );

  return (eventMatches.length ? eventMatches : templates).slice(0, 8);
}

function DiscoveryTemplateCard({ template, onUseTemplate }) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <article className="group relative min-w-[280px] max-w-[320px] flex-1 overflow-hidden rounded-[28px] border border-[#ecdde7] bg-white shadow-[0_18px_40px_rgba(80,41,64,0.08)]">
      <div className="relative aspect-[3/4] overflow-hidden bg-[#f9f2f6]">
        <TemplateAutoVideo
          template={template}
          className="h-full w-full"
          mediaClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          fallbackClassName="flex h-full w-full items-center justify-center bg-[#f6e4ed] text-sm font-bold text-[#9a7186]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8d2f62] shadow-sm">
          Video First
        </div>
        <button
          type="button"
          onClick={() => setIsVideoOpen(true)}
          className="absolute inset-0 flex items-center justify-center"
          aria-label={`Play ${template.name}`}
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/88 text-[#8d2f62] shadow-xl transition-transform duration-300 group-hover:scale-105">
            <PlayCircle size={34} />
          </span>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">
            Invitation Film
          </div>
          <h3 className="mt-2 text-xl font-black leading-tight">{template.name}</h3>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <p className="text-sm leading-6 text-[#735f6d]">
          Preview the video style first, then start customizing it for your celebration.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setIsVideoOpen(true)}
            className="flex-1 rounded-2xl border border-[#ecd5e2] bg-white px-4 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
          >
            Watch Video
          </button>
          <button
            type="button"
            onClick={onUseTemplate}
            className="flex-1 rounded-2xl bg-[#ff0099] px-4 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black"
          >
            Use Template
          </button>
        </div>
      </div>

      <TemplateVideoModal
        template={template}
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        onUseTemplate={onUseTemplate}
      />
    </article>
  );
}

export default function DiscoveryPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState("wedding");
  const [featuredVideoOpen, setFeaturedVideoOpen] = useState(false);
  const rowsRef = useRef({});

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const data = await getTemplates();
        setTemplates(data || []);
      } catch (error) {
        console.error("Failed to load discovery templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return templates;
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    return templates.filter((template) => getTemplateSearchText(template).includes(normalizedQuery));
  }, [searchQuery, templates]);

  const selectedCategory = useMemo(
    () => EVENT_CATEGORIES.find((category) => category.id === selectedEventType) || EVENT_CATEGORIES[0],
    [selectedEventType]
  );

  const selectedCategoryTemplates = useMemo(
    () => templates.filter((template) => doesTemplateMatchCategory(template, selectedEventType)),
    [selectedEventType, templates]
  );

  const featuredTemplate = useMemo(() => {
    const explicitlyFeaturedForCategory = selectedCategoryTemplates.find((template) => template.is_featured);
    if (explicitlyFeaturedForCategory) {
      return explicitlyFeaturedForCategory;
    }

    if (selectedCategoryTemplates.length) {
      return selectedCategoryTemplates[0];
    }

    const explicitlyFeatured = templates.find((template) => template.is_featured);
    if (explicitlyFeatured) {
      return explicitlyFeatured;
    }

    return templates[0] || null;
  }, [selectedCategoryTemplates, templates]);

  const featuredRows = useMemo(
    () =>
      FEATURED_ROWS.map((row) => ({
        ...row,
        templates: pickTemplatesForRow(filteredTemplates, row),
      })),
    [filteredTemplates]
  );

  const handleUseTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (isAuthenticated) {
      navigate(`/editor/${templateId}`, {
        state: {
          mode: "event",
          eventType: selectedEventType,
        },
      });
      return;
    }
    setShowLogin(true);
  };

  const handleLoginSuccess = () => {
    if (!selectedTemplateId) return;
    navigate(`/editor/${selectedTemplateId}`, {
      state: {
        mode: "event",
        eventType: selectedEventType,
      },
    });
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedEventType(categoryId);
    const rowElement = rowsRef.current[categoryId];
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/templates", { state: { eventType: categoryId } });
    }
  };

  return (
    <div className="min-h-screen bg-[#fff7fb] text-[#251f25]">
      <section className="overflow-hidden border-b border-[#f1dfe9] bg-[radial-gradient(circle_at_top,_rgba(255,207,226,0.5),_transparent_32%),linear-gradient(180deg,#fff8fb_0%,#fff2f8_100%)]">
        <div className="mx-auto grid w-full max-w-[1380px] gap-10 px-4 py-14 md:px-8 xl:grid-cols-[1.15fr_0.85fr] xl:items-center xl:gap-14 xl:px-10 xl:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#f0d5e2] bg-white/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#a13469] shadow-sm">
              <Sparkles size={14} />
              Video-first invitations with guest-aware journeys
            </div>
            <h1 className="mt-6 font-[Georgia] text-[clamp(2.4rem,5vw,4.9rem)] font-black leading-[0.98] text-[#231723]">
              Invitation films
              <br />
              that feel personal
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-[#6f5a67] md:text-lg">
              Discover cinematic digital invitations, preview the video style before you choose,
              and build a guest experience that can include event chapters, private media, RSVP,
              comments, and personalized access.
            </p>

            <div className="mt-8 flex flex-col gap-4 md:flex-row">
              <div className="flex flex-1 items-center gap-3 rounded-full border border-[#efcddd] bg-white px-5 py-3 shadow-lg shadow-[#f3d7e4]/70">
                <Search className="h-5 w-5 text-[#b13771]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by event type, mood, or template style"
                  className="w-full bg-transparent text-sm text-[#3d2e38] outline-none placeholder:text-[#b29aa8] md:text-base"
                />
              </div>
              <button
                type="button"
                onClick={() => navigate("/templates", { state: { eventType: selectedEventType, searchQuery } })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-xl shadow-[#ff0099]/20 transition-colors hover:bg-black md:text-base"
              >
                Browse Templates
                <ArrowRight size={18} />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {EVENT_CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isActive = selectedEventType === category.id;
                const templateCount = getCategoryTemplateCount(templates, category.id);

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setSelectedEventType(category.id)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      isActive
                        ? "bg-[#ff0099] text-white shadow-md shadow-[#ff0099]/20"
                        : "bg-white text-[#834d69] hover:bg-[#fff0f7]"
                    }`}
                  >
                    <Icon size={16} />
                    {category.title}
                    {templateCount ? (
                      <span className={isActive ? "text-white/75" : "text-[#b889a2]"}>
                        {templateCount}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {PLATFORM_HIGHLIGHTS.map((item) => (
                <div
                  key={item.value}
                  className="rounded-[24px] border border-[#efd8e4] bg-white/80 p-4 shadow-sm"
                >
                  <strong className="block text-sm font-black text-[#4a2338]">{item.value}</strong>
                  <span className="mt-1 block text-xs leading-5 text-[#806777]">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[34px] border border-[#efd9e4] bg-white/80 p-5 shadow-[0_28px_70px_rgba(112,48,84,0.12)] backdrop-blur">
            <div className="overflow-hidden rounded-[28px] border border-[#f2dce8] bg-[#fdf4f8]">
              <div className="relative aspect-[4/5]">
                <TemplateAutoVideo
                  template={featuredTemplate}
                  className="h-full w-full"
                  mediaClassName="h-full w-full object-cover"
                  fallbackClassName="flex h-full w-full items-center justify-center bg-[#f6e4ed] text-[#9a7186]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#8d2f62]">
                  Featured film
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">
                    {featuredTemplate?.event_type
                      ? `${featuredTemplate.event_type.replace(/_/g, " ")} preview`
                      : `${selectedCategory.title} preview`}
                  </div>
                  <h2 className="max-w-md text-2xl font-black leading-tight">
                    {featuredTemplate?.name || "Cinematic invitation preview"}
                  </h2>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => featuredTemplate && setFeaturedVideoOpen(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-[#892f61] shadow-lg transition-transform hover:-translate-y-0.5"
                    >
                      <PlayCircle size={18} />
                      Watch Video
                    </button>
                    <button
                      type="button"
                      onClick={() => featuredTemplate && handleUseTemplate(featuredTemplate.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black"
                    >
                      Use Template
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-[24px] bg-white px-4 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#b07c98]">
                  Selected occasion
                </p>
                <p className="mt-2 text-sm leading-6 text-[#745f6c]">
                  {selectedCategory.title}: preview the invitation film, then customize schedule,
                  media, guests, and access in the guided flow.
                </p>
              </div>
              <div className="hidden rounded-full bg-[#fff1f8] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#b0356f] md:block">
                Motion-led
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1380px] px-4 py-14 md:px-8 xl:px-10">
        <div className="mb-8 flex items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#b07c98]">
              Start with the occasion
            </p>
            <h2 className="mt-2 text-[clamp(1.7rem,2.5vw,2.5rem)] font-black text-[#251a24]">
              Choose the type of event you are creating
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate("/templates")}
            className="hidden items-center gap-2 rounded-full border border-[#edd4e1] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] md:inline-flex"
          >
            Browse all templates
            <ArrowRight size={16} />
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {EVENT_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const templateCount = getCategoryTemplateCount(templates, category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleCategorySelect(category.id)}
                className={`group overflow-hidden rounded-[30px] border text-left shadow-[0_18px_50px_rgba(86,43,66,0.08)] transition-transform hover:-translate-y-1 ${
                  selectedEventType === category.id
                    ? "border-[#ff0099] bg-white"
                    : "border-[#f0dce7] bg-white"
                }`}
              >
                <div className={`h-2 w-full bg-gradient-to-r ${category.accent}`} />
                <div className="p-6">
                  <div className="mb-4 inline-flex rounded-2xl bg-[#fff2f8] p-3 text-[#a33169]">
                    <Icon size={22} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-2xl font-black text-[#2c1c29]">{category.title}</h3>
                    <span className="rounded-full bg-[#fff7fb] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                      {templateCount ? `${templateCount} films` : "Explore"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-[#735f6d]">{category.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1380px] space-y-14 px-4 pb-16 md:px-8 xl:px-10">
        {isLoading ? (
          <div className="rounded-[30px] border border-[#f0dce7] bg-white p-10 text-center text-[#8a7080] shadow-sm">
            Loading featured invitation films...
          </div>
        ) : (
          featuredRows.map((row) => (
            <div
              key={row.id}
              ref={(element) => {
                rowsRef.current[row.id] = element;
              }}
              className="space-y-6"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="max-w-3xl">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                    Featured Collection
                  </p>
                  <h2 className="mt-2 text-[clamp(1.55rem,2.2vw,2.2rem)] font-black text-[#241824]">
                    {row.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-[#735f6d]">{row.blurb}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/templates", { state: { eventType: row.id, searchQuery } })}
                  className="inline-flex items-center gap-2 rounded-full border border-[#edd4e1] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                >
                  See more
                  <ArrowRight size={16} />
                </button>
              </div>

              <div className="no-scrollbar flex gap-5 overflow-x-auto pb-2">
                {row.templates.map((template) => (
                  <DiscoveryTemplateCard
                    key={`${row.id}-${template.id}`}
                    template={template}
                    onUseTemplate={() => handleUseTemplate(template.id)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      {showLogin ? (
        <LoginModal
          defaultValue=""
          onClose={() => setShowLogin(false)}
          onSuccess={handleLoginSuccess}
        />
      ) : null}

      <TemplateVideoModal
        template={featuredTemplate}
        isOpen={featuredVideoOpen}
        onClose={() => setFeaturedVideoOpen(false)}
        onUseTemplate={() => featuredTemplate && handleUseTemplate(featuredTemplate.id)}
      />
    </div>
  );
}
